/**
 * Hook to initialize native push notifications
 *
 * Uses @capacitor-firebase/messaging for push notifications on iOS/Android
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { nativePushService } from '@/services/native-push.service';

export function useNativePush() {
  const { user } = useAuth();
  const initializedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (user && initializedForUserRef.current !== user.id) {
      initializedForUserRef.current = user.id;
      console.log('[useNativePush] Initializing push notifications...');

      nativePushService.initialize(user.id).then((success) => {
        if (success) {
          console.log('[useNativePush] Push notifications initialized successfully');
        } else {
          console.log('[useNativePush] Push initialization failed:', nativePushService.lastError);
        }
      });
    }

    if (!user) {
      initializedForUserRef.current = null;
    }
  }, [user]);

  return {
    isSupported: nativePushService.isSupported(),
    getPermissionStatus: () => nativePushService.getPermissionStatus(),
    requestPermission: () => nativePushService.requestPermission(),
  };
}

/**
 * Hook to clean up push token on logout
 */
export async function cleanupPushOnLogout(): Promise<void> {
  await nativePushService.removeDeviceToken();
}
